/**
 * Rail v3: visibility aligned with `pages/_components/menu/Menu.jsx`; item order per v3 product.
 * Legacy `MenuSection` sub-items → flyout / mobile `subItems` (`MenuNavItem`).
 * Labels via `getNavLabel` (i18n + fallbacks en `navRailLabelFallback.js`); UI kit icons (`svgNames`).
 */
import { push } from "react-router-redux";

import { CORPORATE_GROUP_ENVIRONMENT_TYPE, ENVIRONMENT_TYPE_CORPORATE } from "constants.js";
import { actions as tokenActions } from "reducers/token";
import * as config from "util/config";
import { canAccessMassEcheqManagementList } from "pages/v2/echeqMassManagement/EcheqsMassManageList";
import { getMultipleTransfersI18nKey } from "util/multiple-transfers";
import { getNavLabel } from "v3/v3UiKitI18n";
import { getRequestCredinPath, getTransferHistoricPath, getV3PrivateNavAccountsPath } from "util/v3UxConfig";
import { getServicePaymentsPath } from "util/servicePaymentsRoutes";
import isTokenActive from "util/token";
import dispatchPrivateNavPreNavigateResetActions from "v3/layout/privateNavPreNavigateEffects";
import { getMetrocorpConsultPath } from "util/metrocorpRoutes";

/**
 * @typedef {{ id: string, labelKey: string, path: string }} SubItemDef
 */

/**
 * @param {object} activeEnvironment
 * @returns {SubItemDef[]}
 */
function buildTransferSubSeeds(activeEnvironment) {
    const perm = activeEnvironment?.permissions || {};
    const newAgendaEnable = config.getBoolean("frontend.show.NuevoComponenteAgenda.enabled", false);
    const hasTransfersHistoricPermission = perm.transferAccess;
    const improvements = config.getBoolean("frontend.show.MultipleTransfers.improvements.functionalities", false);
    const showMultipleTransfers = config.getBoolean("frontend.show.MultipleTransfers.functionalities");

    const subs = [];
    if (perm.frequentDestination) {
        subs.push({
            id: "transfer-frequent",
            labelKey: "frequentDestinationList.main.label",
            path: newAgendaEnable ? "/v2/agenda" : "/FrequentDestination",
        });
    }
    if (hasTransfersHistoricPermission) {
        subs.push({ id: "transfer-historic", labelKey: "transfers.main.label", path: getTransferHistoricPath() });
    }
    if (!improvements && showMultipleTransfers && perm.transferMultiple) {
        subs.push({
            id: "transfer-multiple",
            labelKey: getMultipleTransfersI18nKey("menu.multiple.transfers"),
            path: "/transfer/multiple",
        });
    }
    if (perm["send.credin"]) {
        subs.push({ id: "transfer-credin", labelKey: "credin.main.label", path: getRequestCredinPath() });
    }
    return subs;
}

export function getTransferPrimaryPath(activeEnvironment) {
    const subs = buildTransferSubSeeds(activeEnvironment);
    return subs.length ? subs[0].path : null;
}

/**
 * @param {object} activeEnvironment
 * @returns {SubItemDef[]}
 */
function buildChecksSubSeeds(activeEnvironment) {
    const perm = activeEnvironment?.permissions || {};
    const subs = [];
    if (perm.requestCheckbook) {
        subs.push({ id: "checks-request", labelKey: "menu.checks.requestCheckbook", path: "/checks" });
    }
    if (perm["operate.echeqs"]) {
        subs.push({ id: "checks-echeqs", labelKey: "menu.checks.echeqs", path: "/echeqs" });
    }
    if (perm["operate.echeqs"]) {
        subs.push({
            id: "checks-echeqs-control",
            labelKey: "menu.checks.echeqsControl",
            path: "/v2/echeqs/control",
        });
    }
    if (perm["checks.detail.view"]) {
        subs.push({ id: "checks-detail", labelKey: "menu.checks.list.checksDetail", path: "/checksDetail" });
    }
    if (perm["rule.access.echeq"]) {
        subs.push({ id: "checks-rules", labelKey: "menu.checks.rule.manage", path: "/v2/echeqRules" });
    }
    if (canAccessMassEcheqManagementList(perm)) {
        subs.push({
            id: "checks-mass-echeq-manage",
            labelKey: "menu.checks.massiveEcheq.manage",
            path: "/v2/echeqs/mass-management",
        });
    }
    return subs;
}

function getChecksPrimaryPath(activeEnvironment) {
    const subs = buildChecksSubSeeds(activeEnvironment);
    return subs.length ? subs[0].path : null;
}

/**
 * @param {object} activeEnvironment
 * @returns {SubItemDef[]}
 */
function buildInvestmentsSubSeeds(activeEnvironment) {
    const perm = activeEnvironment?.permissions || {};
    const showCedip = config.getBoolean("backend.show.cedipmvp1.functionalities");
    const enableFundsImprovements = config.getBoolean("frontend.show.FundImprovements.functionalities", false);
    const currencyExchangeEnabled = config.getBoolean("frontend.foreign.currency.trading.enable", false);
    const hasCurrencyExchangePermission =
        perm["investment.currencyExchange.buy"] ||
        perm["investment.currencyExchange.sell"] ||
        perm["investment.currencyExchange"];

    const subs = [];
    if (perm.deposits) {
        subs.push({ id: "inv-deposits", labelKey: "menu.v3.deposits.myDeposits", path: "/deposits" });
    }
    if (perm.fundcorpOperations) {
        subs.push({ id: "inv-fundcorp", labelKey: "menu.v3.invesments.tradeFunds", path: "/fundcorpOperations" });
    }
    if (perm.fundcorpInformation && enableFundsImprovements) {
        subs.push({ id: "inv-fund-info", labelKey: "menu.fundcorp.fundInformation", path: "/fundInformation" });
    }
    if (showCedip && perm.cedip) {
        subs.push({ id: "inv-cedip", labelKey: "menu.investments.manageCedip", path: "/cedip" });
    }
    if (perm.fundcorpInformation && !enableFundsImprovements) {
        subs.push({ id: "inv-fund-info-legacy", labelKey: "menu.fundcorp.fundInformation", path: "/fundInformation" });
    }
    if (currencyExchangeEnabled && hasCurrencyExchangePermission) {
        subs.push({
            id: "inv-fx",
            labelKey: "menu.investments.currencyExchange",
            path: "/v2/investment/currencyExchange",
        });
    }
    return subs;
}

function getInvestmentsPrimaryPath(activeEnvironment) {
    const subs = buildInvestmentsSubSeeds(activeEnvironment);
    return subs.length ? subs[0].path : null;
}

/**
 * @param {object} activeEnvironment
 * @returns {SubItemDef[]}
 */
function buildPaymentsSubSeeds(activeEnvironment) {
    const perm = activeEnvironment?.permissions || {};
    const improvements = config.getBoolean("frontend.show.MultipleTransfers.improvements.functionalities", false);
    const showMultipleTransfers = config.getBoolean("frontend.show.MultipleTransfers.functionalities");
    const newLabel = config.getBoolean("frontend.Show.ContextualMenu.Functionalities", false);

    const subs = [];
    if (perm["pay.debin"]) {
        subs.push({ id: "pay-debin", labelKey: "menu.payments.payDebin", path: "/payments/payDebin" });
    }
    if (perm["pay.third.parties"]) {
        subs.push({ id: "pay-third", labelKey: "menu.payments.payThirdParties", path: "/payThirdParties/list" });
    }
    if (perm["pay.afip"] && perm["service.payments.view"]) {
        subs.push({
            id: "pay-services",
            labelKey: `menu.payments.payServices${newLabel ? ".arca" : "Afip"}`,
            path: getServicePaymentsPath(),
        });
    }
    if (improvements && showMultipleTransfers && perm.transferMultiple) {
        subs.push({
            id: "pay-multiple",
            labelKey: getMultipleTransfersI18nKey("menu.multiple.transfers"),
            path: "/transfer/multiple",
        });
    }
    return subs;
}

function getPaymentsPrimaryPath(activeEnvironment) {
    const subs = buildPaymentsSubSeeds(activeEnvironment);
    return subs.length ? subs[0].path : null;
}

/**
 * @param {object} activeEnvironment
 * @param {boolean} isMobile
 * @param {boolean} isRetailEnvironment
 * @returns {SubItemDef[]}
 */
function buildEcomexSubSeeds(activeEnvironment, isMobile, isRetailEnvironment) {
    const perm = activeEnvironment?.permissions || {};
    const ecomexMobileEnabled = config.getBoolean("ecomex.access.from.app.enabled", false);
    const showEcomexNewRequest = !ecomexMobileEnabled
        ? !isMobile && config.getBoolean("backend.show.ecomexTransaccional.newRequests")
        : config.getBoolean("backend.show.ecomexTransaccional.newRequests");
    const showEcomexRequestHistory = !ecomexMobileEnabled
        ? !isMobile && config.getBoolean("backend.show.ecomexTransaccional.requestHistory")
        : config.getBoolean("backend.show.ecomexTransaccional.newRequests");

    const subs = [];
    if (showEcomexNewRequest && perm.ecomexNewRequests) {
        subs.push({ id: "ecomex-new", labelKey: "menu.ecomex.enterRequests", path: "/ecomex/newRequests" });
    }
    if (showEcomexRequestHistory && perm.ecomexRequestHistory) {
        subs.push({ id: "ecomex-history", labelKey: "menu.ecomex.requestsHistory", path: "/ecomex/requestHistory" });
    }
    if (perm.ecomexConsultOperations) {
        subs.push({
            id: "ecomex-consult",
            labelKey: "menu.ecomex.consultOperations",
            path: "/ecomex/consultOperations",
        });
    }
    if (perm.ecomexInformativeRegime && !isRetailEnvironment) {
        subs.push({
            id: "ecomex-informative",
            labelKey: "menu.ecomex.informativeRegime",
            path: "/ecomex/informativeRegime",
        });
    }
    if (perm.ecomexAlertsAndDues && !isRetailEnvironment) {
        subs.push({
            id: "ecomex-alerts",
            labelKey: "menu.ecomex.alertsAndDues",
            path: "/ecomex/alertsAndDues",
        });
    }
    return subs;
}

export function getEcomexPrimaryPath(activeEnvironment, isMobile, isRetailEnvironment) {
    const subs = buildEcomexSubSeeds(activeEnvironment, isMobile, isRetailEnvironment);
    return subs.length ? subs[0].path : null;
}

/**
 * @param {object} activeEnvironment
 * @returns {SubItemDef[]}
 */
function buildChargesSubSeeds(activeEnvironment) {
    const perm = activeEnvironment?.permissions || {};
    const directDebitMVP1Enable = config.getBoolean("frontend.show.DirectDebitRequest.MVP1.functionalities", false);
    const subs = [];
    if (perm.charges) {
        subs.push({ id: "charges-debin", labelKey: "menu.generateDebin", path: "/charges/generateDebin" });
    }
    if (perm["directDebit.access.menu"] && directDebitMVP1Enable) {
        subs.push({ id: "charges-dd", labelKey: "menu.directDebit", path: "/v2/directDebit/list" });
    }
    return subs;
}

function getChargesPrimaryPath(activeEnvironment) {
    const subs = buildChargesSubSeeds(activeEnvironment);
    return subs.length ? subs[0].path : null;
}

/**
 * @param {object} activeEnvironment
 * @returns {SubItemDef[]}
 */
function buildAdministrationSubSeeds(activeEnvironment) {
    const scheme = activeEnvironment.administrationScheme;
    if (scheme === "simple") {
        return [];
    }
    const subs = [{ id: "adm-users", labelKey: "administration.menu.users", path: "/administration/users" }];
    if (scheme !== "medium") {
        subs.push({ id: "adm-groups", labelKey: "administration.menu.groups", path: "/administration/groups" });
    }
    subs.push({
        id: "adm-signatures",
        labelKey: "administration.menu.signatureScheme",
        path: `/administration/${scheme}/signaturesSchemes`,
    });
    subs.push({
        id: "adm-restrictions",
        labelKey: "administration.restrictions.environment.title",
        path: "/administration/restrictions",
    });
    return subs;
}

function getAdministrationPrimaryPath(activeEnvironment) {
    if (activeEnvironment.administrationScheme === "simple") {
        return "/administration/users";
    }
    const subs = buildAdministrationSubSeeds(activeEnvironment);
    return subs.length ? subs[0].path : "/administration/users";
}

/**
 * @param {SubItemDef[]} defs
 * @returns {SubItemDef[]|undefined}
 */
function subItemDefsForRail(defs) {
    return defs.length > 0 ? defs : undefined;
}

/**
 * Maps the current URL to the rail section id (must match the `id` values generated below).
 *
 * @param {string} pathname
 * @param {boolean} [isMobile] — On mobile, `/settings` and `/settings/token` map under the rail Profile item.
 * @returns {string}
 */
export function sectionIdFromPathname(pathname, isMobile = false) {
    if (!pathname) {
        return "home";
    }
    const p = pathname;
    if (p.startsWith("/v3/dashboard")) {
        return "home";
    }
    if (
        p.startsWith("/tokenPassword") ||
        p.startsWith("/tokenRandomCode") ||
        p.startsWith("/settings/token/tokenRandomCode")
    ) {
        return "token";
    }
    if (
        p.startsWith("/accounts") ||
        p.startsWith("/v2/accounts") ||
        p.startsWith("/v3/accounts") ||
        p.startsWith("/v3/account/create") ||
        p.startsWith("/accountOpening")
    ) {
        return "accounts";
    }
    if (p.startsWith("/v2/corporateCard")) {
        return "corporateCard";
    }
    if (
        p.startsWith("/FrequentDestination") ||
        p.startsWith("/v2/agenda") ||
        p === "/transfer" ||
        p.startsWith("/v2/transfer") ||
        p.startsWith("/v3/transfer") ||
        p.startsWith("/transfer/multiple") ||
        p === "/requestCredin" ||
        p.startsWith("/v3/credin") ||
        p.startsWith("/v3/requestCredin")
    ) {
        return "transfers";
    }
    if (
        p.startsWith("/cheqs") ||
        p.startsWith("/echeqs") ||
        p.startsWith("/v2/echeqs") ||
        p.startsWith("/checks") ||
        p.startsWith("/v2/echeqRules") ||
        p === "/checksDetail" ||
        p === "/discountedChecksDetail"
    ) {
        return "checks";
    }
    if (
        p.startsWith("/deposits") ||
        p.startsWith("/fundcorpOperations") ||
        p === "/fundInformation" ||
        p === "/cedip" ||
        p.startsWith("/cedip/") ||
        p.startsWith("/v2/investment/") ||
        p.startsWith("/subscribe") ||
        p.startsWith("/rescue/") ||
        p.startsWith("/cancelSubscribe") ||
        p.startsWith("/registerShareAccount") ||
        p.startsWith("/currencyExchangeConfirmation")
    ) {
        return "investments";
    }
    if (p.startsWith("/financing")) {
        return "financing";
    }
    if (p.startsWith("/ecomex")) {
        return "ecomex";
    }
    if (
        p === "/v2/payments-services" ||
        p.startsWith("/payments") ||
        p.startsWith("/payThirdParties") ||
        p.startsWith("/v2/payThirdParties") ||
        p === "/servicePayments" ||
        p.startsWith("/payments/recurrent")
    ) {
        return "payments";
    }
    if (p.startsWith("/charges") || p.startsWith("/directDebit") || p.startsWith("/v2/directDebit")) {
        return "charges";
    }
    if (p.startsWith("/pendingTransaction")) {
        return "pendingDispatch";
    }
    if (p.startsWith("/metrocorp") || p.startsWith("/v3/metrocorp")) {
        return "metrocorp";
    }
    if (p.startsWith("/transactions")) {
        return "transactions";
    }
    if (p.startsWith("/settings/token")) {
        return isMobile ? "profile" : "settingsToken";
    }
    if (p.startsWith("/administration")) {
        return "administration";
    }
    if (p.startsWith("/settings") || p.startsWith("/accountsOfficer")) {
        return isMobile ? "profile" : "settings";
    }
    if (p.startsWith("/fixedTermAcceptance")) {
        return "fixedTermAcceptance";
    }
    return "";
}

/**
 * @param {object} opts
 * @param {object|null} opts.activeEnvironment
 * @param {boolean} opts.isRetailEnvironment
 * @param {boolean} opts.isAdministrator
 * @param {boolean} opts.isMobile
 * @param {boolean} opts.featureSignatureDispatchControl
 * @param {(() => void)|undefined} opts.onLogout — Log out (mobile rail item only).
 * @returns {Array<object>}
 */
function computeMenuSeeds({
    activeEnvironment,
    isRetailEnvironment,
    isAdministrator,
    isMobile,
    featureSignatureDispatchControl,
    onLogout,
}) {
    if (!activeEnvironment) {
        return [];
    }

    const perm = activeEnvironment.permissions || {};
    const seeds = [];

    // 1. Home
    seeds.push({ id: "home", labelKey: "menu.desktop", icon: "Home", path: "/v3/dashboard" });

    // CMF Token (not in product order; kept after Home). Siempre en móvil para el bottom nav (home + token);
    // si no hay token activo en el dispositivo, se navega a la configuración del token.
    if (isTokenActive()) {
        const useBiometricTokenAccess = config.getBoolean("backend.show.Biometric.Token.offline");
        if (useBiometricTokenAccess) {
            seeds.push({
                id: "token",
                labelKey: "menu.v3.bottomNav.token",
                icon: "ShieldCheck",
                onNavigate: (dispatch) => {
                    dispatch(tokenActions.biometricTokenAccess());
                },
            });
        } else {
            seeds.push({
                id: "token",
                labelKey: "menu.v3.bottomNav.token",
                icon: "ShieldCheck",
                path: "/tokenPassword",
            });
        }
    } else if (isMobile) {
        seeds.push({
            id: "token",
            labelKey: "menu.v3.bottomNav.token",
            icon: "ShieldCheck",
            path: "/settings/token",
        });
    }

    // 2. My accounts
    seeds.push({
        id: "accounts",
        labelKey: "menu.accounts.myAccounts",
        icon: "Accounts",
        path: getV3PrivateNavAccountsPath(),
    });

    // 3. Corporate card
    const showCorporateCardSummary = config.getBoolean("frontend.show.corporateCard.summary", false);
    if (showCorporateCardSummary && perm["corporateCard.summary"] && !isRetailEnvironment) {
        const corpCardSubs = [
            {
                id: "corp-card-statements",
                labelKey: "menu.corporateCard.accountStatements",
                path: "/v2/corporateCard/accountStatements",
            },
        ];
        seeds.push({
            id: "corporateCard",
            labelKey: "menu.corporateCard",
            icon: "CreditCard",
            path: "/v2/corporateCard/accountStatements",
            subItemDefs: subItemDefsForRail(corpCardSubs),
        });
    }

    // 4. Authorizations (formerly pending transactions)
    if (activeEnvironment.type !== CORPORATE_GROUP_ENVIRONMENT_TYPE && featureSignatureDispatchControl) {
        seeds.push({
            id: "pendingDispatch",
            labelKey: "menu.v3.rail.authorizations",
            icon: "WritingPending",
            path: "/pendingTransaction/list",
        });
    }

    // 5. Transfers
    const transferSubs = buildTransferSubSeeds(activeEnvironment);
    if (transferSubs.length > 0) {
        seeds.push({
            id: "transfers",
            labelKey: "menu.transfers",
            icon: "Transfer",
            path: getTransferPrimaryPath(activeEnvironment),
            subItemDefs: subItemDefsForRail(transferSubs),
        });
    }

    // 6. Payments
    const paymentSubs = buildPaymentsSubSeeds(activeEnvironment);
    if (paymentSubs.length > 0) {
        seeds.push({
            id: "payments",
            labelKey: "menu.payments",
            icon: "CoinDollar",
            path: getPaymentsPrimaryPath(activeEnvironment),
            subItemDefs: subItemDefsForRail(paymentSubs),
        });
    }

    // 7. Checks
    const checksSubs = buildChecksSubSeeds(activeEnvironment);
    if (checksSubs.length > 0) {
        seeds.push({
            id: "checks",
            labelKey: "menu.checks",
            icon: "AddCheq",
            path: getChecksPrimaryPath(activeEnvironment),
            subItemDefs: subItemDefsForRail(checksSubs),
        });
    }

    // 8. Investments (móvil corporativo: mismo ítem "Inversiones" con submenú, p. ej. Operar con fondos)
    const invSubs = buildInvestmentsSubSeeds(activeEnvironment);
    if (invSubs.length > 0) {
        seeds.push({
            id: "investments",
            labelKey: "menu.investments",
            icon: "StocksUp",
            path: getInvestmentsPrimaryPath(activeEnvironment),
            subItemDefs: subItemDefsForRail(invSubs),
        });
    }

    // 9. Collections
    const chargesSubs = buildChargesSubSeeds(activeEnvironment);
    if (chargesSubs.length > 0) {
        seeds.push({
            id: "charges",
            labelKey: "menu.charges",
            icon: "BagDollar",
            path: getChargesPrimaryPath(activeEnvironment),
            subItemDefs: subItemDefsForRail(chargesSubs),
        });
    }

    // 10. Financing
    if (perm.financingList) {
        const financingSubs = [
            {
                id: "financing-categories",
                labelKey: "menu.financing.categories",
                path: "/financing/categories",
            },
        ];
        seeds.push({
            id: "financing",
            labelKey: "menu.financing",
            icon: "Briefcase",
            path: "/financing/categories",
            subItemDefs: subItemDefsForRail(financingSubs),
        });
    }

    // 11. Foreign trade
    const ecomexSubs = buildEcomexSubSeeds(activeEnvironment, isMobile, isRetailEnvironment);
    if (ecomexSubs.length > 0) {
        seeds.push({
            id: "ecomex",
            labelKey: "menu.ecomex",
            icon: "MapSearch",
            path: getEcomexPrimaryPath(activeEnvironment, isMobile, isRetailEnvironment),
            subItemDefs: subItemDefsForRail(ecomexSubs),
        });
    }

    // 12. Metrocorp
    if (perm.metrocorp) {
        const metrocorpPath = getMetrocorpConsultPath();
        const metrocorpSubs = [
            {
                id: "metrocorp-consult",
                labelKey: "menu.metrocorp.consultOperations",
                path: metrocorpPath,
            },
        ];
        seeds.push({
            id: "metrocorp",
            labelKey: "menu.metrocorp",
            icon: "GraphLine",
            path: metrocorpPath,
            subItemDefs: subItemDefsForRail(metrocorpSubs),
        });
    }

    // 13. Token settings
    if (!isMobile) {
        seeds.push({
            id: "settingsToken",
            labelKey: "settings.token",
            icon: "ShieldCheck",
            path: "/settings/token",
        });
    }

    // 14. Transaction history
    seeds.push({
        id: "transactions",
        labelKey: "menu.transactions",
        icon: "Refund",
        path: "/transactions/list",
    });

    // 15. Administration
    const showAdminTab = !isMobile && isAdministrator && !isRetailEnvironment;
    if (showAdminTab) {
        const admSubs = buildAdministrationSubSeeds(activeEnvironment);
        const isSimpleScheme = activeEnvironment.administrationScheme === "simple";
        seeds.push({
            id: "administration",
            labelKey: "administration.menu.name",
            icon: "BriefcaseNext",
            path: getAdministrationPrimaryPath(activeEnvironment),
            subItemDefs: !isSimpleScheme ? subItemDefsForRail(admSubs) : undefined,
        });
    }

    // 16. (mobile only) Profile — same routes as header user menu; before web fixed-term acceptance
    if (isMobile) {
        seeds.push({
            id: "profile",
            labelKey: "menu.settings",
            icon: "UserCircle",
            path: "/settings",
            subItemDefs: [
                { id: "profile-config", labelKey: "header.user.menu.goToConfig", path: "/settings" },
                {
                    id: "profile-official",
                    labelKey: "header.user.menu.goToAccountOfficial",
                    path: "/accountsOfficer",
                },
                { id: "profile-token", labelKey: "settings.token", path: "/settings/token" },
            ],
        });
    }

    // 17. Web fixed-term acceptance
    seeds.push({
        id: "fixedTermAcceptance",
        labelKey: "menu.fixedTermAcceptance",
        icon: "TrendUp",
        path: "/fixedTermAcceptance",
    });

    // 18. (mobile only) Log out — always last
    if (isMobile && typeof onLogout === "function") {
        seeds.push({
            id: "logout",
            labelKey: "header.user.menu.handleLogout",
            icon: "SendOut",
            onNavigate: onLogout,
        });
    }

    return seeds;
}

/**
 * @param {object} opts
 * @param {string} opts.selectedSectionId
 * @param {(id: string) => void} opts.setSelectedSectionId
 * @param {import("redux").Dispatch} opts.dispatch
 * @param {{ push: (path: string) => void }} opts.history
 * @param {string} [opts.pathname] current `location.pathname` (legacy `Menu` parity for `onSelect` guards)
 * @param {object|null} opts.activeEnvironment
 * @param {boolean} opts.isRetailEnvironment
 * @param {boolean} opts.isAdministrator
 * @param {boolean} opts.isMobile
 * @param {boolean} opts.featureSignatureDispatchControl
 * @param {() => void} [opts.onLogout]
 */
export default function buildPrivateNavItems({
    selectedSectionId,
    setSelectedSectionId,
    dispatch,
    history,
    pathname = "",
    activeEnvironment,
    isRetailEnvironment,
    isAdministrator,
    isMobile,
    featureSignatureDispatchControl,
    onLogout,
}) {
    const seeds = computeMenuSeeds({
        activeEnvironment,
        isRetailEnvironment,
        isAdministrator,
        isMobile,
        featureSignatureDispatchControl,
        onLogout,
    });

    const perm = activeEnvironment?.permissions || {};

    const items = seeds.map((seed) => {
        const hasSubNav = Boolean(seed.subItemDefs && seed.subItemDefs.length > 0);

        const item = {
            id: seed.id,
            label: getNavLabel(seed.labelKey),
            icon: seed.icon,
            isSelected: seed.id === selectedSectionId,
            onClick: () => {
                if (hasSubNav && seed.id === selectedSectionId) {
                    setSelectedSectionId("");
                } else {
                    setSelectedSectionId(seed.id);
                }
                if (typeof seed.onNavigate === "function") {
                    seed.onNavigate(dispatch, history);
                } else if (seed.path && !hasSubNav) {
                    dispatchPrivateNavPreNavigateResetActions(dispatch, pathname, seed.path);
                    dispatch(push(seed.path));
                }
            },
        };

        if (seed.subItemDefs && seed.subItemDefs.length > 0) {
            item.subItems = seed.subItemDefs.map((sub) => ({
                id: sub.id,
                label: getNavLabel(sub.labelKey),
                onClick: () => {
                    dispatchPrivateNavPreNavigateResetActions(dispatch, pathname, sub.path);
                    dispatch(push(sub.path));
                },
            }));
        }

        if (isMobile && !isRetailEnvironment && perm.fundcorpOperations && seed.id === "investments") {
            item.bottomNavLabel = getNavLabel("menu.v3.bottomNav.funds");
            const fundcorpSub = seed.subItemDefs && seed.subItemDefs.find((s) => s.id === "inv-fundcorp");
            const fundcorpPath = (fundcorpSub && fundcorpSub.path) || "/fundcorpOperations";
            item.bottomNavOnClick = () => {
                setSelectedSectionId(seed.id);
                dispatchPrivateNavPreNavigateResetActions(dispatch, pathname, fundcorpPath);
                dispatch(push(fundcorpPath));
            };
        }

        if (isMobile && isRetailEnvironment && perm.metrocorp && seed.id === "metrocorp" && seed.path) {
            item.bottomNavOnClick = () => {
                setSelectedSectionId(seed.id);
                dispatchPrivateNavPreNavigateResetActions(dispatch, pathname, seed.path);
                dispatch(push(seed.path));
            };
        }

        return item;
    });

    if (!isMobile) {
        return items;
    }

    const isCorporateType = activeEnvironment?.type === ENVIRONMENT_TYPE_CORPORATE;
    const hasPendingSeed = seeds.some((s) => s.id === "pendingDispatch");
    const hasTransfersSeed = seeds.some((s) => s.id === "transfers");
    let mainAccessId = null;
    if (isCorporateType && hasPendingSeed) {
        mainAccessId = "pendingDispatch";
    } else if (hasTransfersSeed) {
        mainAccessId = "transfers";
    }

    if (!mainAccessId) {
        return items;
    }

    return items.map((it) => ({
        ...it,
        isMainAccessMobile: it.id === mainAccessId,
    }));
}
